import PropTypes from 'prop-types';
import cogoToast from 'cogo-toast';
import { confirmAlert } from 'react-confirm-alert';
import './BrandCancellationsQueue.scss';

import { cancelScheduledBrandCancellation } from '../../api/brand_cancellations';
import { CATEGORY_CONFIGS, OVERDUE_GRACE_MS, TASK_TRIGGER_LABELS, formatTaskDateET } from '../../helpers/brand_cancellation_helpers';

const BrandCancellationsQueue = ({ tasks, isLoading, onRefresh, onTasksChanged }) => {
  const hasProcessingTasks = tasks.some(task => task.status === 'PROCESSING');

  const cancelTask = task => {
    confirmAlert({
      title: 'Cancel this scheduled cancellation?',
      message: `${task.brand?.name || `Brand ${task.brand?.id}`} keeps everything; the queued task is deleted.`,
      buttons: [
        { label: 'Keep it', onClick: () => {}, className: 'cancel' },
        {
          label: 'Cancel it',
          onClick: () =>
            cancelScheduledBrandCancellation(task.taskId)
              .then(() => {
                cogoToast.success('Cancelled.');
                onTasksChanged();
              })
              .catch(err => {
                cogoToast.error(err?.response?.data?.error || 'Could not cancel it — it may already be processing.');
                onTasksChanged();
              })
        }
      ]
    });
  };

  return (
    <aside className='brand-cancellations-queue'>
      <div className='brand-cancellations-queue-header'>
        <h2>Queue & history</h2>
        <button type='button' onClick={onRefresh} disabled={isLoading}>
          {isLoading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {tasks.length === 0 ? (
        <div className='brand-cancellations-queue-empty'>Nothing queued yet.</div>
      ) : (
        tasks.map(task => {
          const isPending = task.status === 'PENDING';
          const isOverdue =
            isPending && !hasProcessingTasks && task.executionTimestamp && task.executionTimestamp * 1000 < Date.now() - OVERDUE_GRACE_MS;
          const flagSummary = CATEGORY_CONFIGS.filter(config => task.flags?.[config.flag])
            .map(config => config.shortLabel)
            .join(', ');
          const queuedBy = TASK_TRIGGER_LABELS[task.trigger] || (task.triggeredBy?.name ? `by ${task.triggeredBy.name}` : '');
          return (
            <div key={task.taskId} className='brand-cancellations-task-card'>
              <div className='brand-cancellations-task-header'>
                <span className='brand-cancellations-task-brand'>{task.brand?.name || `Brand ${task.brand?.id}`}</span>
                <span className={`brand-cancellations-task-status brand-cancellations-task-status-${(task.status || 'unknown').toLowerCase()}`}>
                  {task.status}
                </span>
              </div>
              <div className='brand-cancellations-task-meta'>
                <span>Runs {task.executionTimestamp ? formatTaskDateET(task.executionTimestamp * 1000) : 'immediately'}</span>
                <span>
                  Queued {formatTaskDateET(task.createdAt)}
                  {queuedBy ? ` ${queuedBy}` : ''}
                </span>
              </div>
              {isOverdue && (
                <div className='brand-cancellations-task-overdue'>Past its run time — check that the brand cancellation worker is running.</div>
              )}
              {flagSummary && <div className='brand-cancellations-task-flags'>Removes: {flagSummary}</div>}
              {task.status === 'FAILED' && task.statusMsg && <div className='brand-cancellations-task-error'>{task.statusMsg}</div>}
              {isPending && (
                <button type='button' className='brand-cancellations-task-cancel-btn' onClick={() => cancelTask(task)}>
                  Cancel
                </button>
              )}
            </div>
          );
        })
      )}
    </aside>
  );
};

BrandCancellationsQueue.propTypes = {
  tasks: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onTasksChanged: PropTypes.func.isRequired
};

export default BrandCancellationsQueue;
